import React, { useState, useEffect } from 'react';
import { 
  FileText,Eye, Calendar, User, Pill, 
  AlertCircle, CheckCircle, Clock, Search, Hospital
} from 'lucide-react';

import patientService from '../../services/patientService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Alert from '../../components/ui/Alert';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import Badge from '../../components/ui/Badge';

const Prescriptions = () => {
 

  const [loading, setLoading] = useState(true);
  const [prescriptions, setPrescriptions] = useState([]);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [filters, setFilters] = useState({
    status: '',
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0
  });

  useEffect(() => {
    loadPrescriptions();
  }, [filters, pagination.page]);

  const loadPrescriptions = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        status: filters.status || undefined,
        search: filters.search || undefined,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      };

      const response = await patientService.getPrescriptions(params);
      
      if (response.success) {
        setPrescriptions(response.data?.prescriptions || []);
        setPagination(prev => ({
          ...prev,
          total: response.data?.total || 0
        }));
      }
    } catch (error) {
      setError('Failed to load prescriptions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (prescription) => {
    try {
      setLoading(true);
      const response = await patientService.getPrescriptionById(prescription._id);
      
      if (response.success) {
        setSelectedPrescription(response.data);
        setShowDetailsModal(true);
      }
    } catch (error) {
      setError('Failed to load prescription details.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statuses = {
      active: {
        color: 'bg-green-100 text-green-800',
        icon: CheckCircle,
        label: 'Active'
      },
      expired: {
        color: 'bg-red-100 text-red-800',
        icon: AlertCircle,
        label: 'Expired'
      },
      completed: {
        color: 'bg-gray-100 text-gray-800',
        icon: CheckCircle,
        label: 'Completed'
      },
      pending: {
        color: 'bg-yellow-100 text-yellow-800',
        icon: Clock,
        label: 'Pending'
      }
    };

    return statuses[status] || statuses.active;
  };

  const isExpiringSoon = (validUntil) => {
    if (!validUntil) return false;
    const daysUntilExpiry = Math.ceil(
      (new Date(validUntil) - new Date()) / (1000 * 60 * 60 * 24)
    );
    return daysUntilExpiry > 0 && daysUntilExpiry <= 7;
  };

  const PrescriptionCard = ({ prescription }) => {
    const statusInfo = getStatusInfo(prescription.status);
    const StatusIcon = statusInfo.icon;
    const expiringSoon = isExpiringSoon(prescription.validUntil);

    return (
      <div className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <FileText className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                Prescription #{prescription.prescriptionNumber || prescription._id.slice(-6)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Issued: {new Date(prescription.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          <Badge className={statusInfo.color}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {statusInfo.label}
          </Badge>
        </div>

        {expiringSoon && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center">
              <AlertCircle className="w-4 h-4 text-yellow-600 mr-2" />
              <p className="text-xs text-yellow-800">
                Expires soon on {new Date(prescription.validUntil).toLocaleDateString()}
              </p>
            </div>
          </div>
        )}

        <div className="mb-4 pb-4 border-b">
          <div className="flex items-center mb-2">
            <User className="w-4 h-4 text-gray-400 mr-2" />
            <span className="text-sm font-medium text-gray-900">
              Dr. {prescription.doctor?.firstName} {prescription.doctor?.lastName}
            </span>
          </div>
          <p className="text-xs text-gray-600 ml-6">
            {prescription.doctor?.specialization}
          </p>
          {prescription.hospital && (
            <div className="flex items-center mt-2 ml-6">
              <Hospital className="w-3 h-3 text-gray-400 mr-1" />
              <span className="text-xs text-gray-600">{prescription.hospital.name}</span>
            </div>
          )}
        </div>

        <div className="mb-4">
          <div className="flex items-center mb-2">
            <Pill className="w-4 h-4 text-gray-400 mr-2" />
            <span className="text-sm font-medium text-gray-900">
              Medications ({prescription.medications?.length || 0})
            </span>
          </div>
          <div className="ml-6 space-y-1">
            {prescription.medications?.slice(0, 3).map((med, index) => (
              <p key={index} className="text-xs text-gray-600">
                • {med.name} - {med.dosage} ({med.frequency})
              </p>
            ))}
            {prescription.medications?.length > 3 && (
              <p className="text-xs text-gray-500">
                +{prescription.medications.length - 3} more medication(s)
              </p>
            )}
          </div>
        </div>

        {prescription.diagnosis && (
          <div className="mb-4 pb-4 border-b">
            <p className="text-xs text-gray-600 mb-1">Diagnosis:</p>
            <p className="text-sm text-gray-900">{prescription.diagnosis}</p>
          </div>
        )}

        {prescription.validUntil && (
          <div className="mb-4">
            <div className="flex items-center text-xs text-gray-600">
              <Calendar className="w-3 h-3 mr-1" />
              Valid until: {new Date(prescription.validUntil).toLocaleDateString()}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleViewDetails(prescription)}
          >
            <Eye className="w-3 h-3 mr-1" />
            View Details
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Prescriptions</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and manage your medical prescriptions
          </p>
        </div>
      </div>

      {success && (
        <Alert type="success" title="Success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert type="error" title="Error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Alert type="info" title="About Prescriptions">
        Your prescriptions are issued by licensed doctors during consultations. 
        You can use them to order medicines from partnered pharmacies.
      </Alert>

      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            icon={Search}
            placeholder="Search prescriptions..."
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          />

          <Select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="completed">Completed</option>
          </Select>

          <Select
            value={filters.sortBy}
            onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
          >
            <option value="createdAt">Sort by Date</option>
            <option value="validUntil">Sort by Expiry</option>
            <option value="doctor">Sort by Doctor</option>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : prescriptions.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {prescriptions.map(prescription => (
              <PrescriptionCard key={prescription._id} prescription={prescription} />
            ))}
          </div>

          {pagination.total > pagination.limit && (
            <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border p-4">
              <p className="text-sm text-gray-600">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} prescriptions
              </p>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg border">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">No prescriptions found</p>
          <p className="text-sm text-gray-400">
            Prescriptions from your consultations will appear here
          </p>
        </div>
      )}

      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Prescription Details"
        size="xl"
      >
        {selectedPrescription && (
          <div className="space-y-6">
            <div className="flex items-center justify-between pb-4 border-b">
              <div>
                <p className="text-lg font-semibold text-gray-900">
                  Prescription #{selectedPrescription.prescriptionNumber || selectedPrescription._id.slice(-6)}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Issued on {new Date(selectedPrescription.createdAt).toLocaleDateString()}
                </p>
              </div>
              <Badge className={getStatusInfo(selectedPrescription.status).color}>
                {getStatusInfo(selectedPrescription.status).label}
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Prescribing Doctor</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-medium text-gray-900">
                    Dr. {selectedPrescription.doctor?.firstName} {selectedPrescription.doctor?.lastName}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedPrescription.doctor?.specialization}
                  </p>
                </div>
              </div>

              {selectedPrescription.hospital && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Hospital/Clinic</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="font-medium text-gray-900">
                      {selectedPrescription.hospital.name}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {selectedPrescription.diagnosis && (
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Diagnosis</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-900">{selectedPrescription.diagnosis}</p>
                </div>
              </div>
            )}

            <div>
              <h3 className="font-medium text-gray-900 mb-3">Prescribed Medications</h3>
              <div className="space-y-3">
                {selectedPrescription.medications?.map((med, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{med.name}</p>
                        <div className="mt-2 space-y-1">
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Dosage:</span> {med.dosage}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Frequency:</span> {med.frequency}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Duration:</span> {med.duration}
                          </p>
                          {med.instructions && (
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Instructions:</span> {med.instructions}
                            </p>
                          )}
                        </div>
                      </div>
                      {med.quantity && (
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Quantity</p>
                          <p className="text-lg font-semibold text-gray-900">{med.quantity}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {selectedPrescription.notes && (
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Additional Notes</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-900">{selectedPrescription.notes}</p>
                </div>
              </div>
            )}

            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center">
                <Calendar className="w-5 h-5 text-blue-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Prescription Validity</p>
                  <p className="text-xs text-blue-700 mt-1">
                    Valid until: {selectedPrescription.validUntil 
                      ? new Date(selectedPrescription.validUntil).toLocaleDateString()
                      : 'No expiry date'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Prescriptions;